import { withFaroRouterInstrumentation } from '@grafana/faro-react';
import { ChatInputProps } from '@terminal/components/chat';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { ProtectedView } from '@terminal/components/protected-view/ProtectedView';
import { Root } from '@terminal/Root';
import { FeatureFlag } from '@terminal/stores/use-flags/useFlags';
import { ServerError } from '@terminal/views/error';
import { NotFound } from '@terminal/views/error/not-found/NotFound';
import React, { ReactElement, ReactNode } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  NonIndexRouteObject,
  Route,
  RouterProvider,
} from 'react-router';

import { appPaths } from './app-paths';
import {
  authRoutes,
  basicOrLightOnlyRoutes,
  commonRoutes,
  featureRoutes,
  nonBasicOrLightRoutes,
  nonBasicRoutes,
  routes,
} from './AppRoutes';

export type AppRoute = Omit<NonIndexRouteObject, 'element'> & {
  element: ReactElement<{
    renderChatInput: (props?: ChatInputProps) => ReactNode;
  }>;
  featureFlag?: FeatureFlag;
  crumb?: { isSidebar?: boolean; labelKey?: string; getLabel?: () => string };
  authRequired?: boolean;
};

export const Routes = () => {
  const appRoutes = [
    ...commonRoutes,
    ...featureRoutes,
    ...routes,
    ...authRoutes,
    ...nonBasicRoutes,
    ...nonBasicOrLightRoutes,
    ...basicOrLightOnlyRoutes,
  ];

  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route
        path="/"
        element={
          <ErrorBoundaryWrapper fallback={<ServerError />}>
            <Root />
          </ErrorBoundaryWrapper>
        }
      >
        <Route index element={<Navigate to={appPaths.base} replace />} />
        {appRoutes.map(routeConfig => (
          <Route
            path={routeConfig.path}
            element={<ProtectedView config={routeConfig} />}
            key={routeConfig.path}
          />
        ))}
        <Route path={appPaths.notFound} element={<NotFound />} />
      </Route>
    )
  );

  const browserRouter = withFaroRouterInstrumentation(router);

  return <RouterProvider router={browserRouter} />;
};
